import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { relativeDay } from './ScheduledList.jsx';

// T322 — the one forward-looking date on an application.
//
// Sits beside SalaryPanel in the drawer and deliberately mirrors its shape, but
// the save semantics differ and the difference is the point: a salary band
// accretes detail about one number, so it merges; "the next event" is a single
// fact that gets superseded, so it replaces. Merging would let yesterday's kind
// survive onto tomorrow's date and mislabel it.

const KINDS = [
  { value: 'interview', label: 'Interview' },
  { value: 'screening', label: 'Screening' },
  { value: 'call', label: 'Call' },
  { value: 'follow_up', label: 'Follow up' },
  { value: 'task', label: 'Task for me' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'other', label: 'Something else' },
];

/** Date → the value a `datetime-local` input wants, in LOCAL time. */
export function toLocalInput(d) {
  if (!d) return '';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  // toISOString() would shift by the timezone offset and show the operator a
  // time that is not the one he typed — two hours out in Copenhagen in summer.
  const pad = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
}

export default function NextEventPanel({ rec, onUpdated }) {
  const saved = rec?.nextEvent?.at ? rec.nextEvent : null;

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ at: '', kind: 'interview', note: '' });

  // T353 — one place that puts the form back to what is SAVED, called from
  // every path that leaves the form: walking to another job, Cancel, Clear, and
  // a successful Save.
  //
  // It used to run only on `rec?.id`. So: open Change on an application
  // scheduled for the 5th, type the 12th, press Cancel, press Change again —
  // the field showed the 12th while the line above it said the 5th, and Save
  // then wrote a date the operator had explicitly abandoned. Clear was worse:
  // it wiped the saved event and left the form holding the old one, ready to
  // re-create what had just been deleted.
  const reseed = useCallback((from) => {
    setForm({
      at: toLocalInput(from?.at),
      kind: from?.kind ?? 'interview',
      note: from?.note ?? '',
    });
  }, []);

  // Re-seed when the drawer walks to another job (j/k navigation).
  useEffect(() => {
    setOpen(false);
    reseed(saved);
  }, [rec?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.at) {
      toast('Pick a date — an event with no date is what this field exists to prevent.', { kind: 'error' });
      return;
    }
    setBusy(true);
    try {
      const body = await api.patch(`/recommendations/${rec.id}`, {
        nextEvent: { at: new Date(form.at).toISOString(), kind: form.kind, note: form.note },
      });
      onUpdated?.({ nextEvent: body?.recommendation?.nextEvent ?? null });
      reseed(body?.recommendation?.nextEvent ?? { at: form.at, kind: form.kind, note: form.note });
      setOpen(false);
      toast('Saved — it will show on the dashboard', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Could not save', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      const body = await api.patch(`/recommendations/${rec.id}`, { nextEvent: null });
      onUpdated?.({ nextEvent: body?.recommendation?.nextEvent ?? null });
      reseed(null);
      setOpen(false);
      toast('Cleared', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Could not clear', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const overdue = saved && new Date(saved.at) < new Date();

  return (
    <section className="mt-4">
      <p className="text-[10px] uppercase tracking-widest text-slate-400">Next step</p>

      {saved && !open && (
        <p className="mt-1 text-xs">
          <span className={overdue ? 'font-medium text-amber-400' : 'font-medium text-[var(--c-accent)]'}>
            {KINDS.find((k) => k.value === saved.kind)?.label ?? 'Event'} {relativeDay(saved.at)}
          </span>
          <span className="text-slate-500">
            {' · '}
            {new Date(saved.at).toLocaleString(undefined, {
              weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            })}
          </span>
          {/* Said out loud rather than left to the colour: an event whose date
              has passed and that nobody cleared is the state a job search
              quietly loses applications in. */}
          {overdue && <span className="block text-amber-400">Passed — did it happen?</span>}
          {saved.note ? <span className="block text-slate-500">{saved.note}</span> : null}
        </p>
      )}

      {!saved && !open && (
        <p className="mt-1 text-xs text-slate-500">Nothing scheduled.</p>
      )}

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-1 text-xs text-[var(--c-accent)] underline decoration-dotted hover:opacity-80"
        >
          {saved ? 'Change' : '+ Schedule something'}
        </button>
      ) : (
        <div className="mt-2 space-y-2 rounded-md border border-slate-800 p-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">When</span>
              <input
                type="datetime-local"
                value={form.at}
                onChange={(e) => setForm((f) => ({ ...f, at: e.target.value }))}
                className="mt-0.5 w-full rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-xs"
                aria-label="When"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">What</span>
              <select
                value={form.kind}
                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
                className="mt-0.5 w-full rounded border border-slate-700 bg-slate-900/60 px-1 py-1 text-xs"
                aria-label="What kind of event"
              >
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>
          </div>
          <input
            type="text"
            value={form.note}
            maxLength={500}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="e.g. second round, technical — with Anna"
            className="w-full rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-xs"
            aria-label="Note"
          />
          <div className="flex items-center gap-2">
            <button
              type="button" disabled={busy} onClick={save}
              className="rounded bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-500 disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button" disabled={busy} onClick={() => { reseed(saved); setOpen(false); }}
              className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 disabled:opacity-50"
            >
              Cancel
            </button>
            {saved && (
              <button
                type="button" disabled={busy} onClick={clear}
                className="ml-auto text-xs text-slate-500 underline decoration-dotted hover:text-slate-300"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
